import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { monthLabel, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, PlusCircle, RefreshCw } from "lucide-react";
import { PlantUpload } from "./PlantUpload";
import { PagesPanel } from "./PagesPanel";
import { QuestionsPanel } from "./QuestionsPanel";
import { PlantResults } from "./ResultsView";
import { EntryEditor } from "./EntryEditor";
import { keepVisible, slugify } from "./imageUtils";

const AllLines = ({ plant, sessionId, entries, customers, onChange, locked }) => {
  const [edit, setEdit] = useState(null);
  const list = entries.filter((e) => e.plant === plant);
  const done = () => { setEdit(null); onChange(); };
  return (
    <details data-testid={`all-entries-${slugify(plant)}`} className="bg-white border rounded-xl p-3 sm:p-4">
      <summary className="cursor-pointer font-semibold text-sm py-1">All notebook lines for {plant} ({list.length})</summary>
      <ul className="mt-3 space-y-2">
        {list.map((e) => edit === e.id
          ? <li key={e.id}><EntryEditor entry={e} sessionId={sessionId} plant={plant} customers={customers} onDone={done} onCancel={() => setEdit(null)} /></li>
          : <li key={e.id} className="flex flex-wrap items-center gap-2 text-sm border rounded-md p-2">
              <span className="text-slate-400">P{e.page ?? "-"}</span><span>{e.date ? fmtDate(e.date) : "?"}</span><b>{e.customer_read}</b><span>{e.tons_text} {e.tons != null && `= ${e.tons} t`}</span>
              {e.edited && <span className="text-[11px] text-sky-700">edited{e.original ? ` (AI: ${e.original.customer_read} ${e.original.tons_text})` : ""}</span>}
              {!locked && <Button type="button" size="sm" variant="outline" className="h-10 ml-auto" data-testid={`list-edit-${e.id}`} onClick={() => setEdit(e.id)}>Edit</Button>}
            </li>)}
      </ul>
      {!locked && (edit === "new"
        ? <div className="mt-2"><EntryEditor sessionId={sessionId} plant={plant} customers={customers} onDone={done} onCancel={() => setEdit(null)} /></div>
        : <Button type="button" variant="outline" className="h-11 mt-2" data-testid={`add-entry-${slugify(plant)}`} onClick={() => setEdit("new")}><PlusCircle className="h-4 w-4 mr-1" />Add notebook line</Button>)}
    </details>
  );
};

const Approve = ({ sessionId, blockedReason, openIssues, onApproved }) => {
  const [open, setOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/verification/sessions/${sessionId}/approve`, { remarks }); toast.success("Final report approved and saved. Photos deleted."); onApproved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <section className="bg-white border rounded-xl p-3 sm:p-4 space-y-2">
      {blockedReason ? <p data-testid="approve-blocked-reason" className="text-sm text-amber-800">Final approval locked: {blockedReason}</p> : null}
      {!open ? (
        <Button type="button" data-testid="open-approve-button" disabled={!!blockedReason} className="h-11 bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)}><ShieldCheck className="h-4 w-4 mr-1" />Approve & Save Final Report</Button>
      ) : (
        <div data-testid="approve-panel" className="space-y-2">
          <p className="text-sm">The report, all lines, corrections and results are saved permanently. The notebook photos are then deleted.</p>
          {openIssues > 0 && <p data-testid="approve-open-issues" className="text-sm text-amber-800">{openIssues} discrepancy(ies) are still unresolved and will be saved as open in the report.</p>}
          <Textarea data-testid="approve-remarks" className="text-base" placeholder="Remarks (optional)" value={remarks} onFocus={keepVisible} onChange={(e) => setRemarks(e.target.value)} />
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="h-11" data-testid="approve-cancel" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="button" data-testid="approve-save-button" disabled={busy} className="h-11 bg-emerald-600 hover:bg-emerald-700" onClick={go}>{busy ? "Saving…" : "I Approve — Save Report"}</Button>
          </div>
        </div>
      )}
    </section>
  );
};

export const SessionView = ({ sessionId }) => {
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [data, setData] = useState(null);
  const [cmp, setCmp] = useState(null);
  const sig = useRef("");

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: c }] = await Promise.all([api.get(`/verification/sessions/${sessionId}`), api.get(`/verification/sessions/${sessionId}/compare`)]);
      setData(d); setCmp(c);
      sig.current = d.pages.map((p) => p.status + p.line_count).join();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [sessionId]);

  useEffect(() => { api.get("/plants").then((r) => setPlants(r.data.map((p) => p.name))); load(); }, [load]);

  const scanning = data?.pages.some((p) => ["pending", "processing"].includes(p.status));
  useEffect(() => {
    if (!scanning) return undefined;
    const t = setInterval(async () => {
      const { data: d } = await api.get(`/verification/sessions/${sessionId}`);
      const s = d.pages.map((p) => p.status + p.line_count).join();
      if (s !== sig.current) load(); else setData(d);
    }, 4000);
    return () => clearInterval(t);
  }, [scanning, sessionId, load]);

  if (!data) return <p className="text-slate-400">Loading saved verification…</p>;
  const s = data.session;
  const locked = s.status === "approved";
  const failed = data.pages.filter((p) => p.status === "failed").length;
  const reason = scanning ? "pages are still being read" : failed ? `${failed} page(s) failed — retry them or mark as typed` : !data.pages.length ? "upload notebook pages first" : cmp?.open_questions ? `${cmp.open_questions} question(s) unanswered` : "";
  const shownPlants = Object.keys(cmp?.plants || {});

  return (
    <div data-testid="session-view" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between bg-sky-50 border border-sky-200 rounded-xl p-3">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="icon" className="h-10 w-10" data-testid="back-to-sessions" onClick={() => navigate("/admin/verification")}><ArrowLeft className="h-4 w-4" /></Button>
          <div className="text-sm text-sky-900"><b data-testid="session-month">{monthLabel(s.month)}</b> · {data.pages.length} page(s) · {data.entries.length} line(s) · saved automatically</div>
        </div>
        <Button type="button" variant="ghost" className="h-10" data-testid="refresh-session" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>
      {locked && <p data-testid="session-approved" className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900">Approved by {s.approved_by}. The final report is in Saved Reports.</p>}
      {!locked && <div className="grid md:grid-cols-2 gap-3">{plants.map((p) => <PlantUpload key={p} plant={p} sessionId={sessionId} pageCount={data.pages.filter((x) => x.plant === p).length} onUploaded={load} />)}</div>}
      <PagesPanel pages={data.pages} locked={locked} onChange={load} />
      {cmp && !locked && <QuestionsPanel sessionId={sessionId} entries={data.entries} resolution={cmp.resolution} customers={cmp.customers} onChange={load} />}
      {cmp && shownPlants.map((p) => (
        <div key={p} className="space-y-3">
          <PlantResults plant={p} data={cmp.plants[p]} readOnly={locked} sessionId={sessionId} entries={data.entries} customers={cmp.customers} onChange={load} />
          <AllLines plant={p} sessionId={sessionId} entries={data.entries} customers={cmp.customers} onChange={load} locked={locked} />
        </div>
      ))}
      {!locked && <Approve sessionId={sessionId} blockedReason={reason} openIssues={Object.values(cmp?.plants || {}).reduce((n, p) => n + (p.summary.open_discrepancies || 0), 0)} onApproved={load} />}
    </div>
  );
};
